import { useState, useEffect } from 'react';
import { aiDashboardService } from '../services/aiDashboardService';

const CHAT_HISTORY_KEY = 'ai_copilot_history';
const PINNED_CHARTS_KEY = 'ai_pinned_charts';
const MAX_HISTORY_MESSAGES = 20;

export const useAICopilot = () => {
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(CHAT_HISTORY_KEY);
      return saved ? JSON.parse(saved).slice(-MAX_HISTORY_MESSAGES) : [];
    } catch { return []; }
  });

  const [pinnedCharts, setPinnedCharts] = useState(() => {
    try {
      const saved = localStorage.getItem(PINNED_CHARTS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestedQueries, setSuggestedQueries] = useState([]);

  useEffect(() => {
    localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(messages.slice(-MAX_HISTORY_MESSAGES)));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem(PINNED_CHARTS_KEY, JSON.stringify(pinnedCharts));
  }, [pinnedCharts]);

  const sendMessage = async (text, overrides = {}) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      const context = messages.slice(-5); // Send last 5 messages for context
      const res = await aiDashboardService.sendChat(text, context);
      
      const aiMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: res.reply,
        chartConfig: res.chartConfig,
        actions: res.actions,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
      if (res.followUp && res.followUp.length > 0) {
        setSuggestedQueries(res.followUp);
      }
    } catch (err) {
      setError(err.message || 'Failed to send message');
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: "I'm having trouble connecting to the server. Please try again.",
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const retryMessage = (messageId) => {
    const idx = messages.findIndex(m => m.id === messageId);
    if (idx > 0 && messages[idx - 1].sender === 'user') {
      const userMessageText = messages[idx - 1].text;
      // Remove bad response
      setMessages(prev => prev.filter(m => m.id !== messageId));
      // Resend
      sendMessage(userMessageText);
    }
  };

  const pinChart = (message) => {
    if (!message.chartConfig) return;
    if (pinnedCharts.length >= 10) {
      alert("You can only pin up to 10 charts.");
      return;
    }
    const alreadyPinned = pinnedCharts.some(p => p.id === message.id);
    if (!alreadyPinned) {
      setPinnedCharts(prev => [...prev, {
        id: message.id,
        chartConfig: message.chartConfig,
        pinnedAt: new Date().toISOString()
      }]);
    }
  };

  const unpinChart = (id) => {
    setPinnedCharts(prev => prev.filter(chart => chart.id !== id));
  };

  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem(CHAT_HISTORY_KEY);
    setSuggestedQueries([]);
  };

  return {
    messages,
    loading,
    error,
    pinnedCharts,
    suggestedQueries,
    sendMessage,
    retryMessage,
    pinChart,
    unpinChart,
    clearChat
  };
};
